import { cn } from '@/lib/cn';

export interface BrandMarkProps {
  className?: string;
}

/**
 * The vertical gada is the compact/mobile identity and the browser-tab mark.
 */
export function BrandMark({ className }: BrandMarkProps) {
  return (
    <span
      className={cn(
        'grid size-7 shrink-0 place-items-center drop-shadow-[0_1px_2px_rgb(0_0_0/0.45)]',
        className,
      )}
      aria-hidden="true"
    >
      <img
        src="/brand/bheema-gada-vertical-512.png"
        width="512"
        height="512"
        alt=""
        className="size-full object-contain"
      />
    </span>
  );
}

/** The approved desktop lockup keeps the wordmark and gada in one exact asset,
 *  at its approved size (44 px image; the ink is its middle 78%, ~34 px).
 *  Both theme variants are rendered; `index.css` shows the one that matches the
 *  active `data-theme`, so the raster ink keeps its contrast on either surface.
 *
 *  Owner, 2026-09-24: same gada, same lettering — only a little room above and
 *  below it, like the environment badge has. The image is no longer offset and
 *  clipped to a 36 px row (which made the ink touch both edges); the desktop
 *  top bar grew to 47 px instead (`--density-topbar`), centring the ink with
 *  ~5.4 px above and ~6.1 px below. */
export function BrandWordmark({ className }: BrandMarkProps) {
  return (
    <span
      className={cn('relative block h-11 w-[132px] shrink-0', className)}
      data-brand-lockup="true"
      aria-hidden="true"
    >
      <img
        src="/brand/bheema-wordmark-v4-light.png"
        width="2169"
        height="725"
        alt=""
        className="brand-wordmark-light absolute top-0 left-0 h-11 w-auto max-w-none object-contain drop-shadow-[0_1px_2px_rgb(0_0_0/0.24)]"
        data-brand-wordmark="light"
      />
      <img
        src="/brand/bheema-wordmark-v4-dark.png"
        width="2168"
        height="725"
        alt=""
        className="brand-wordmark-dark absolute top-0 left-0 h-11 w-auto max-w-none object-contain drop-shadow-[0_1px_2px_rgb(0_0_0/0.32)]"
        data-brand-wordmark="dark"
      />
    </span>
  );
}

/**
 * A larger, un-clipped lockup for the login screen — the same two theme assets,
 * shown in full and centred, sized for a sign-in hero rather than the 36px bar.
 */
export function BrandLockup({ className }: BrandMarkProps) {
  return (
    <span className={cn('relative inline-block', className)} aria-label="Bheema-PM">
      <img
        src="/brand/bheema-wordmark-v4-light.png"
        width="2169"
        height="725"
        alt="Bheema-PM"
        className="brand-wordmark-light block h-14 w-auto object-contain"
      />
      <img
        src="/brand/bheema-wordmark-v4-dark.png"
        width="2168"
        height="725"
        alt="Bheema-PM"
        className="brand-wordmark-dark block h-14 w-auto object-contain"
      />
    </span>
  );
}
